import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBar, Popup } from 'antd-mobile';
import { LeftOutline } from 'antd-mobile-icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import MapboxMapView from '../../../components/Map/MapboxMapView';
import { DspPath } from '../../../enums/DeliveryNavigatePathEnum';
import DspParcelDetailCard, { DspParcelDetailCardRef } from './components/DspParcelDetailCard';
import { GET_DSP_DELIVERY_ORDERS_GROUPED } from '../graphql/queries/getDspDeliveryOrdersGrouped';
import { MapViewMode, DeliveryOrder, GetDspDeliveryOrdersGroupedData } from '../graphql/types/DeliveryTaskType';
import { formatStopNumber } from '../../../utils/stopNumberUtils';
import { MapBoxPageMarker } from '../../../constants/mapBoxTrackLog';
import {closeNavigationModals as closeNavigationModalsUtil, forceCloseNavigationModal, emitCloseNavigationEvent} from '../../../utils/navigationUtils';

// 颜色配置
const COLOR_PALETTE = [
    '#3366CC', // 亮蓝
    '#009688', // 湖绿
    '#FFD700', // 金黄色
    '#8E44AD', // 紫色
    '#00BFFF', // 天蓝
    '#FFA500', // 橙色
    '#43A047', // 深绿
    '#5C6BC0', // 靛蓝
    '#00CEC9', // 湖蓝
    '#B2BEC3', // 银灰
    '#16A085', // 青色
    '#27AE60', // 草绿色
    '#2980B9', // 深蓝
    '#E1BEE7', // 淡紫
    '#FFD54F'  // 柠檬黄
];

// 格式化日期为 YYYY-MM-DD 格式
const formatDateForAPI = (dateStr: string): string => {
    // 如果已经是 YYYY-MM-DD 格式，直接返回
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
    }
    
    // 如果是 DD/MM/YYYY 格式，转换为 YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return dateStr;
};

const DspDeliveryTaskMapPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    
    // 地图视图模式状态
    const [viewMode, setViewMode] = useState<MapViewMode>(MapViewMode.BY_ROUTE);

    // 获取当前任务日期（从路由状态或默认值）
    const getTaskDate = (): string => {
        // 从路由状态中获取日期
        if (location.state && location.state.taskDate) {
            return location.state.taskDate;
        }
        
        // 默认使用今天的日期
        const today = new Date();
        const day = today.getDate().toString().padStart(2, '0');
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const year = today.getFullYear();
        return `${day}/${month}/${year}`;
    };
    
    const [taskDate] = useState<string>(getTaskDate());

    // 获取选中的司机ID列表（从sessionStorage）
    const getSelectedDriverIds = (): number[] | undefined => {
        const savedDriverIds = sessionStorage.getItem('selectedDriverIds');
        if (savedDriverIds) {
            try {
                const driverIds = JSON.parse(savedDriverIds);
                if (Array.isArray(driverIds) && driverIds.length > 0) {
                    return driverIds.map(id => Number(id)).filter(id => !isNaN(id));
                }
            } catch (error) {
                console.warn('Failed to parse saved driver IDs:', error);
            }
        }
        return undefined;
    };
    
    // Popup 控制状态
    const [popupVisible, setPopupVisible] = useState(false);
    const [selectedMarkerData, setSelectedMarkerData] = useState<(DeliveryOrder & { clusterInfo?: any }) | null>(null);
    const dspParcelDetailCardRef = useRef<DspParcelDetailCardRef>(null);

    // 使用GraphQL查询获取数据
    const { data, loading, error } = useQuery<GetDspDeliveryOrdersGroupedData>(GET_DSP_DELIVERY_ORDERS_GROUPED, {
        variables: {
            taskDate: formatDateForAPI(taskDate),
            groupBy: viewMode === MapViewMode.BY_ROUTE ? 'ROUTE' : 'DRIVER',
            driverIds: getSelectedDriverIds()
        }
    });
    
    
    // 构建运单数据Map用于快速查找
    const ordersMap = useMemo(() => {
        if (!data?.getDspDeliveryOrdersGrouped) return new Map<string, DeliveryOrder>();
        
        const map = new Map<string, DeliveryOrder>();
        data.getDspDeliveryOrdersGrouped.groups.forEach(group => {
            group.orders.forEach(order => {
                map.set(order.id, order);
            });
        });
        return map;
    }, [data]);
    
    // 根据视图模式分组运单
    const groupedOrders = useMemo(() => {
        if (!data?.getDspDeliveryOrdersGrouped) return {};
        
        const groups: { [key: string]: DeliveryOrder[] } = {};
        data.getDspDeliveryOrdersGrouped.groups.forEach(group => {
            groups[group.groupKey] = group.orders;
        });
        return groups;
    }, [data]);
    
    // 为每个分组分配颜色
    const groupColors = useMemo(() => {
        const colors: { [key: string]: string } = {};
        const groupKeys = Object.keys(groupedOrders);
        groupKeys.forEach((key, index) => {
            colors[key] = COLOR_PALETTE[index % COLOR_PALETTE.length];
        });
        return colors;
    }, [groupedOrders]);
    
    // 转换为地图组件需要的格式
    const mapAddresses = useMemo(() => {
        const addresses: any[] = [];
        
        Object.entries(groupedOrders).forEach(([groupKey, orders]) => {
            const color = groupColors[groupKey];
            
            orders.forEach(order => {
                addresses.push({
                    id: order.id,
                    address: order.address,
                    trackingNumber: order.trackingNumber,
                    deliveryTaskOrderId: parseInt(order.id),
                    stopNumber: order.stopNumber,
                    subStopNumber: order.subStopNumber,
                    isAbnormal: false,
                    localMarkerColor: color,
                    latitude: order.latitude,
                    longitude: order.longitude
                });
            });
        });

        return addresses;
    }, [groupedOrders, groupColors]);
    
    
    // 处理返回按钮
    const handleBack = () => {
        // 设置标记，表示从派送任务相关页面跳转
        sessionStorage.setItem('fromDeliveryTask', 'true');
        
        navigate(DspPath.DELIVERY_DSP_TASK);
    };

    // 统一关闭导航弹窗逻辑（iOS 18 增强修复）
    const handleCloseNavigationModals = useCallback(() => {
        // 方式1：通过全局事件重置所有卡片组件的状态（解决集群模式问题）
        emitCloseNavigationEvent();
        // 方式2：单个包裹模式：通过 ref 关闭
        dspParcelDetailCardRef.current?.closeLongPressModal();
        // 方式3：集群模式：直接调用工具函数关闭所有导航弹窗（确保集群模式下也能关闭，iOS 18 增强）
        closeNavigationModalsUtil();
        // iOS 18 需要更强制的方式关闭弹窗，使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
        requestAnimationFrame(() => {
            forceCloseNavigationModal(
                t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                t('pages.delivery.orderDetail.mapCancel') || undefined
            );
        });
    }, [t]);

    // 统一关闭弹窗逻辑（包含 iOS 18 增强修复）
    const handleClosePopup = useCallback(() => {
        // 关闭长按导航弹窗
        handleCloseNavigationModals();
        
        setPopupVisible(false);
        setSelectedMarkerData(null);
    }, [handleCloseNavigationModals]);
    
    // 处理视图模式切换
    const handleViewModeChange = (value: string) => {
        setViewMode(value as MapViewMode);
        // 切换视图模式时关闭弹窗
        handleClosePopup();
    };
    
    // 处理标记点击事件 - 显示弹窗
    const handleMarkerClick = (markerData: any) => {
        // 关闭长按导航弹窗
        handleCloseNavigationModals();
        
        // 检查是否是集群数据
        if (markerData.isCluster && markerData.clusterAddresses) {
            // 将集群地址数据转换为 DeliveryOrder 格式
            const clusterOrders = markerData.clusterAddresses.map((addr: any) => {
                const order = ordersMap.get(addr.id);
                if (order) {
                    return {
                        ...order,  // 以 order 为主（包含所有业务字段）
                        // 只覆盖地图特有的字段
                        latitude: addr.latitude,           // 地图计算的精确坐标
                        longitude: addr.longitude,         // 地图计算的精确坐标
                        deliveryTaskOrderId: addr.deliveryTaskOrderId, // 地图使用的ID格式
                        stopNumber: formatStopNumber(parseInt(order.stopNumber), order.subStopNumber) // 格式化 stopNumber
                    };
                }
                return null;
            }).filter(Boolean);

            if (clusterOrders.length > 0) {
                setSelectedMarkerData({
                    ...clusterOrders[0], // 使用第一个订单作为主要数据
                    clusterInfo: {
                        isCluster: true,
                        clusterCount: clusterOrders.length,
                        clusterAddresses: clusterOrders
                    }
                });
                setPopupVisible(true);
            }
        } else {
            // 单个订单处理
            const order = ordersMap.get(markerData.id);
            if (order) {
                setSelectedMarkerData({
                    ...order,
                    stopNumber: formatStopNumber(parseInt(order.stopNumber), order.subStopNumber) // 格式化 stopNumber
                });
                setPopupVisible(true);
            }
        }
        // 强制滚动到顶部
        setTimeout(() => {
            // 方法1：查找 Popup 内的滚动容器
            const popupBody = document.querySelector('.adm-popup-body');
            if (popupBody) {
                popupBody.scrollTop = 0;
            }

            // 方法2：查找内部的滚动容器（集群模式使用 max-h-[70vh]）
            const scrollContainer = document.querySelector('.max-h-\\[70vh\\]');
            if (scrollContainer) {
                scrollContainer.scrollTop = 0;
            }

            // 方法3：直接查找所有可滚动元素并重置
            const scrollableElements = document.querySelectorAll('*');
            scrollableElements.forEach(element => {
                if (element.scrollHeight > element.clientHeight) {
                    element.scrollTop = 0;
                }
            });
        }, 0);
    };
    
    // 处理地图点击事件 - 关闭弹窗和长按导航弹窗
    const handleMapClick = () => {
        handleClosePopup();
    };
    
    return (
        <div className="flex flex-col h-screen bg-white">
            {/* 顶部导航栏 */}
            <div className="flex-none sticky top-0 z-10 bg-white shadow-sm">
                <NavBar
                    backIcon={<LeftOutline fontSize={20} />}
                    onBack={handleBack}
                >
                    {t('pages.dsp.deliveryTaskMap.title')}
                </NavBar>
            </div>
            
            {/* 地图容器 */}
            <div className="flex-1 relative">
                {loading ? (
                    <div className="flex items-center justify-center h-full bg-gray-50">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <div className="text-gray-600">{t('pages.dsp.deliveryTaskMap.loadingMapData')}</div>
                            <div className="text-gray-400 text-sm mt-2">{t('pages.dsp.deliveryTaskMap.largeDataWarning')}</div>
                        </div>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <div className="text-red-500 text-lg mb-2">{t('pages.dsp.deliveryTaskMap.loadFailed')}</div>
                            <div className="text-gray-600">{error.message}</div>
                        </div>
                    </div>
                ) : mapAddresses.length > 0 ? (
                    <MapboxMapView
                        deliveryAddresses={mapAddresses}
                        onMarkerClick={handleMarkerClick}
                        onMapClick={handleMapClick}
                        showCurrentLocation={false}
                        pageMarker={`${MapBoxPageMarker.DSP_MAP_MODE}_${viewMode}`}
                    />
                ) : (
                    <div className="flex items-center justify-center h-full bg-gray-50">
                        <div className="text-center">
                            <div className="text-gray-400 text-lg mb-2">{t('pages.dsp.deliveryTaskMap.noData')}</div>
                            <div className="text-gray-500 text-sm">{t('pages.dsp.deliveryTaskMap.noOrderData')}</div>
                        </div>
                    </div>
                )}
                
                {/* 悬浮的视图模式切换按钮 */}
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1001]">
                    <div className="bg-white rounded-full shadow-lg border border-gray-200 overflow-hidden">
                        <div className="flex">
                            <button
                                onClick={() => handleViewModeChange(MapViewMode.BY_ROUTE)}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${
                                    viewMode === MapViewMode.BY_ROUTE
                                        ? 'bg-blue-500 text-white rounded-full'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                {t('pages.dsp.deliveryTaskMap.byRoute')}
                            </button>
                            <button
                                onClick={() => handleViewModeChange(MapViewMode.BY_DRIVER)}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${
                                    viewMode === MapViewMode.BY_DRIVER
                                        ? 'bg-blue-500 text-white rounded-full'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                {t('pages.dsp.deliveryTaskMap.byDriver')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Popup 弹窗 */}
            <Popup
                visible={popupVisible}
                position="bottom"
                mask={false}
                bodyStyle={{
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    maxHeight: '80vh'
                }}
                onClose={handleClosePopup}
            >
                {selectedMarkerData && (
                    <DspParcelDetailCard
                        ref={dspParcelDetailCardRef}
                        parcel={selectedMarkerData}
                        onClose={handleClosePopup}
                    />
                )}
            </Popup>
        </div>
    );
};

export default DspDeliveryTaskMapPage;
